/**
 * useExtraction — unified hook for document extraction upload + polling.
 *
 * Replaces per-app polling logic from HybridTariffUploader and aiUploadService.
 * Uploads to Platform API /api/v1/extract/upload, then polls /api/v1/extract/jobs/:id.
 */
import { useState, useCallback, useEffect, useRef } from 'react';
import { API_BASE_URL } from '../../services/api';
import { useAuth } from '../../auth';

export function useExtraction({ onComplete, onError, pollInterval = 5000 } = {}) {
    const { currentUser } = useAuth();
    const [jobId, setJobId] = useState(null);
    const [status, setStatus] = useState('idle'); // idle | uploading | processing | complete | error
    const [progress, setProgress] = useState(0);
    const [statusMessage, setStatusMessage] = useState('');
    const [result, setResult] = useState(null);
    const [error, setError] = useState(null);
    const pollingRef = useRef(null);

    // Upload file and start extraction
    const upload = useCallback(async (file, documentType, { customLabel, projectId, metadata } = {}) => {
        if (!currentUser) {
            setStatus('error');
            setError('Not authenticated');
            return null;
        }

        setStatus('uploading');
        setProgress(0);
        setError(null);
        setResult(null);

        const formData = new FormData();
        formData.append('file', file);
        formData.append('document_type', documentType);
        if (customLabel) formData.append('custom_label', customLabel);
        if (projectId) formData.append('project_id', projectId);
        if (metadata) formData.append('metadata', JSON.stringify(metadata));

        try {
            const token = await currentUser.getIdToken();
            const res = await fetch(`${API_BASE_URL}/api/v1/extract/upload`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` },
                body: formData,
            });

            if (!res.ok) {
                const err = await res.json().catch(() => ({}));
                throw new Error(err.error || `Upload failed (${res.status})`);
            }

            const data = await res.json();
            setJobId(data.job_id);
            setStatus('processing');
            return data.job_id;
        } catch (err) {
            setStatus('error');
            setError(err.message);
            onError?.(err);
            return null;
        }
    }, [currentUser, onError]);

    // Poll job status
    useEffect(() => {
        if (status !== 'processing' || !jobId || !currentUser) return;

        const poll = async () => {
            try {
                const token = await currentUser.getIdToken();
                const res = await fetch(`${API_BASE_URL}/api/v1/extract/jobs/${jobId}`, {
                    headers: { 'Authorization': `Bearer ${token}` },
                });

                if (!res.ok) throw new Error('Failed to fetch job status');

                const job = await res.json();
                setProgress(job.progress || 0);
                setStatusMessage(job.status_message || '');

                if (job.status === 'complete') {
                    setStatus('complete');
                    setResult(job.extracted_data || job);
                    onComplete?.(job);
                    return; // Stop polling
                }

                if (job.status === 'failed') {
                    setStatus('error');
                    setError(job.error || 'Extraction failed');
                    onError?.(new Error(job.error || 'Extraction failed'));
                    return; // Stop polling
                }

                // Continue polling
                pollingRef.current = setTimeout(poll, pollInterval);
            } catch (err) {
                setStatus('error');
                setError(err.message);
                onError?.(err);
            }
        };

        poll();
        return () => clearTimeout(pollingRef.current);
    }, [status, jobId, currentUser, pollInterval, onComplete, onError]);

    const reset = useCallback(() => {
        clearTimeout(pollingRef.current);
        setJobId(null);
        setStatus('idle');
        setProgress(0);
        setStatusMessage('');
        setResult(null);
        setError(null);
    }, []);

    return { upload, reset, jobId, status, progress, statusMessage, result, error };
}
